import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  IndametaTag,
  IndametaV2DataStructureResponse,
  IndametaV2SearchResponse,
  IndametaV2TypesResponse
} from '@core/models/indameta.model';
import { environment } from '@env/environment';
import { ModalSearchTagForm } from '@shared/modal/models/modal.model';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class IndametaService {
  private http = inject(HttpClient);

  private baseUrlV2 = environment.iobaseIndaMetaV2Url;
  private selectedGroup = 'Transport';

  public selectedGroupId: string = null;
  public metricTypeId: string = null;
  public ouvrageTypeId: string = null;
  public territoireTypeId: string = null;

  public getDataStructureIdAndTypesId(): Observable<void> {
    return this.http
      .get<IndametaV2DataStructureResponse>(`${this.baseUrlV2}data-structures?name=${this.selectedGroup}`)
      .pipe(
        switchMap(r => {
          if (r.resultCode === 2010 && r.data.length >= 1) {
            this.selectedGroupId = r.data[0].id;

            return this.http
              .get<IndametaV2TypesResponse>(`${this.baseUrlV2}data-structures/${this.selectedGroupId}/types`)
              .pipe(
                map(r => {
                  if (r.resultCode === 2010 && r.data.length >= 1) {
                    const dataStructureTypes = r.data;
                    this.metricTypeId = dataStructureTypes.find(prop => prop.name === 'Métriques')?.id;
                    this.ouvrageTypeId = dataStructureTypes.find(prop => prop.name === 'Ouvrage')?.id;
                    this.territoireTypeId = dataStructureTypes.find(prop => prop.name === 'Territoire')?.id;
                  }
                })
              );
          }
        })
      );
  }

  public isTagExist(tag: string): Observable<IndametaV2SearchResponse> {
    return this.http.get<IndametaV2SearchResponse>(
      `${this.baseUrlV2}data-structures/${this.selectedGroupId}/items?typeId=${this.metricTypeId}&limit=10&displayParents=true&query=Name=="${tag}"`
    );
  }

  public isOuvrageExist(codeOuvrage: string): Observable<IndametaV2SearchResponse> {
    return this.http.get<IndametaV2SearchResponse>(
      `${this.baseUrlV2}data-structures/${this.selectedGroupId}/items?typeId=${this.ouvrageTypeId}&limit=10&displayParents=true&query=Id=="${codeOuvrage}"`
    );
  }

  public searchV2(search: ModalSearchTagForm): Observable<Array<IndametaTag>> {
    const query = this.buildSearchQuery(search);
    let params = new HttpParams();

    if (query !== '') {
      params = params.append('query', query);
    }
    params = params.append('isMetric', true);
    params = params.append('displayParents', true);
    params = params.append('limit', 500);

    return this.http
      .get<IndametaV2SearchResponse>(`${this.baseUrlV2}data-structures/${this.selectedGroupId}/items`, { params })
      .pipe(
        map(resp => {
          return resp.data
            .map(
              d =>
                ({
                  ouvrageCode: d?.parents[this.ouvrageTypeId]?.[0]?.id ? d.parents[this.ouvrageTypeId][0].id : '',
                  tag: d?.metric?.name ? d.metric.name : '',
                  ouvrageVille: d?.parents[this.ouvrageTypeId]?.[0]?.ville
                    ? d.parents[this.ouvrageTypeId][0].ville.charAt(0).toUpperCase() +
                      d.parents[this.ouvrageTypeId][0].ville.slice(1)
                    : '',
                  unite: d?.metric?.unit ? d.metric.unit : '',
                  description: d?.metric?.description ? d.metric.description : '',
                  ouvrageDescription: d?.parents[this.ouvrageTypeId]?.[0]?.label
                    ? d.parents[this.ouvrageTypeId][0].label
                    : '',
                  territoireLibelle: d?.parents[this.territoireTypeId]?.[0]?.label
                    ? d.parents[this.territoireTypeId][0].label
                    : '',
                  calculatedTagId: d?.options?.indabaId.endsWith('.calc') ? d.options.indabaId : '',
                  lastValue: null
                }) as IndametaTag
            )
            .sort((a, b) => a.tag.localeCompare(b.tag));
        })
      );
  }

  public buildSearchQuery(search: ModalSearchTagForm): string {
    const queryArray: string[] = [];

    if (search.ouvrage) {
      queryArray.push(`searchProperty("${this.ouvrageTypeId}", "Id").contains("${search.ouvrage}")`);
    }
    if (search.unite) {
      queryArray.push(`Unit.contains("${search.unite}")`);
    }
    if (search.tagDescription) {
      queryArray.push(`Description.contains("${search.tagDescription}")`);
    }
    if (search.ouvrageDescription) {
      queryArray.push(`searchProperty("${this.ouvrageTypeId}", "Label").contains("${search.ouvrageDescription}")`);
    }
    if (search.ville) {
      queryArray.push(`searchProperty("${this.ouvrageTypeId}", "ville").contains("${search.ville}")`);
    }
    if (search.territoire) {
      queryArray.push(`searchProperty("${this.territoireTypeId}", "Label").contains("${search.territoire}")`);
    }
    if (search.tag) {
      if (search.tag.includes('|')) {
        queryArray.push(
          '(' +
            search.tag
              .split('|')
              .map(x => `Name.contains("${x}")`)
              .join('||') +
            ')'
        );
      } else {
        queryArray.push(`Name.contains("${search.tag}")`);
      }
    }

    const query = queryArray.join('&&');

    return query;
  }
}
